import { FC, KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const COLLECTION_NAME_MAX_LENGTH = 120;
const COLLECTION_DESCRIPTION_MAX_LENGTH = 250;

interface CollectionFormProps {
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
  hasError: string;
  setHasError: (error: string) => void;
  isLoading?: boolean;
  onSubmit?: () => void;
  nameLabel?: string;
}

export const CollectionForm: FC<CollectionFormProps> = ({
  name,
  setName,
  description,
  setDescription,
  hasError,
  setHasError,
  isLoading = false,
  onSubmit,
  nameLabel = 'Collection Name',
}) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!name?.trim()) {
      return;
    }

    if (event.key === 'Enter' && onSubmit) {
      onSubmit();
    }
  };

  return (
    <div className='space-y-4'>
      <div className='space-y-2'>
        <Label className='label-required' htmlFor='collection-name'>
          {nameLabel}
        </Label>
        <Input
          id='collection-name'
          autoFocus
          value={name}
          onChange={(e) => {
            setHasError('');
            setName(e.target.value);
          }}
          placeholder='Enter collection name...'
          disabled={isLoading}
          error={hasError}
          onKeyDown={handleKeyDown}
          maxLength={COLLECTION_NAME_MAX_LENGTH}
        />
        <p className='text-xs text-gray-500'>
          Only letters, numbers, spaces, hyphens, and underscores are allowed
          (max {COLLECTION_NAME_MAX_LENGTH} characters)
        </p>
      </div>

      <div className='space-y-2'>
        <Label htmlFor='collection-description'>Description</Label>
        <Textarea
          id='collection-description'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder='Enter collection description...'
          className='min-h-[80px] resize-none'
          disabled={isLoading}
          maxLength={COLLECTION_DESCRIPTION_MAX_LENGTH}
        />
        <p className='text-xs text-gray-500 text-right'>
          {description.length}/{COLLECTION_DESCRIPTION_MAX_LENGTH} characters
        </p>
      </div>
    </div>
  );
};

// Shared validation logic
export const validateCollectionName = (name: string): string => {
  const trimmedName = name.trim();

  if (!trimmedName) {
    return 'Collection name is required';
  }

  if (trimmedName.length > COLLECTION_NAME_MAX_LENGTH) {
    return `Collection name cannot exceed ${COLLECTION_NAME_MAX_LENGTH} characters`;
  }

  if (!trimmedName.match(/^[a-zA-Z0-9 _-]+$/)) {
    return 'Collection name cannot contain special characters';
  }

  return '';
};
